/**
 * Cascade-delete an invoice and its linked records.
 */

export interface DeleteInvoiceParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: any;
  invoiceId: string;
  deletePayments?: boolean;
  deleteTreatmentRecords?: boolean;
}

export interface DeleteInvoiceResult {
  deletedInvoice: boolean;
  deletedPayments: number;
  deletedTreatmentRecords: number;
  error?: string;
}

export async function deleteInvoiceCascade(
  params: DeleteInvoiceParams,
): Promise<DeleteInvoiceResult> {
  const { db, invoiceId, deletePayments = true, deleteTreatmentRecords = true } = params;

  const result: DeleteInvoiceResult = {
    deletedInvoice: false,
    deletedPayments: 0,
    deletedTreatmentRecords: 0,
  };

  // 1. Verify invoice exists
  const invRows = await db.getAll(`SELECT id, status FROM invoices WHERE id = ?`, [invoiceId]);
  if ((invRows as unknown[]).length === 0) {
    result.error = 'Invoice not found';
    return result;
  }

  // 2. Get invoice item IDs (needed for treatment record lookup)
  const itemRows = (await db.getAll(`SELECT id FROM invoice_items WHERE invoice_id = ?`, [
    invoiceId,
  ])) as { id: string }[];
  const itemIds = itemRows.map((r: { id: string }) => r.id);

  // 3. Delete payments if requested
  if (deletePayments) {
    const paymentRows = (await db.getAll(`SELECT id FROM invoice_payments WHERE invoice_id = ?`, [
      invoiceId,
    ])) as { id: string }[];
    result.deletedPayments = paymentRows.length;
    await db.execute(`DELETE FROM invoice_payments WHERE invoice_id = ?`, [invoiceId]);
  }

  // 4. Delete treatment records linked to this invoice or its items
  if (deleteTreatmentRecords) {
    // By invoice_id
    const byInvoice = (await db.getAll(
      `SELECT id FROM patient_treatment_records WHERE invoice_id = ?`,
      [invoiceId],
    )) as { id: string }[];
    let trCount = byInvoice.length;
    await db.execute(`DELETE FROM patient_treatment_records WHERE invoice_id = ?`, [invoiceId]);

    // By invoice_item_id
    for (const itemId of itemIds) {
      const byItem = (await db.getAll(
        `SELECT id FROM patient_treatment_records WHERE invoice_item_id = ?`,
        [itemId],
      )) as { id: string }[];
      trCount += byItem.length;
      await db.execute(`DELETE FROM patient_treatment_records WHERE invoice_item_id = ?`, [itemId]);
    }
    result.deletedTreatmentRecords = trCount;
  }

  // 5. Delete invoice items (always)
  await db.execute(`DELETE FROM invoice_items WHERE invoice_id = ?`, [invoiceId]);

  // 6. Delete the invoice
  await db.execute(`DELETE FROM invoices WHERE id = ?`, [invoiceId]);
  result.deletedInvoice = true;

  return result;
}
